import React from 'react';

import { MediaInfoFragment } from 'generated/graphql';

import { isImage, isPdf, isVideo } from 'lib/media';
import { useIsShowroomMobileVersion } from 'lib/showroom';

import Typography from '@material-ui/core/Typography';

import ShowroomImage from 'components/Common/ShowroomImage';
import { ComponentProps as PictureProps } from 'components/Common/ShowroomImage/ShowroomImage';
import VideoPlayer from 'components/Common/VideoPlayer';
import ShowroomMediaActions from 'components/Showroom/MediaActions';

import { useStyles } from './styles';

export type ComponentProps = {
	media: MediaInfoFragment;
	displayLabel: boolean;
	layerEnabled: boolean;
	onMediaAction(cloudinaryId: string, type: 'image' | 'video'): void;
	imageProps?: Partial<PictureProps>;
};

const ShowroomMediaItem = (props: ComponentProps) => {
	const { media, displayLabel, layerEnabled, imageProps } = props;
	const { onMediaAction } = props;

	const classes = useStyles({ layerEnabled });
	const isMobileVersion = useIsShowroomMobileVersion();

	const mediaActionHandler = React.useCallback(() => {
		onMediaAction(media.cloudinaryId, !isVideo(media) ? 'image' : 'video');
	}, [media, onMediaAction]);

	const labelComponent = React.useMemo(() => {
		return (
			<div className={classes.previewLabel} id={`showroomMediaLabel_${media.id}`}>
				<Typography component="span" noWrap className={classes.label}>
					{media.name}
				</Typography>
			</div>
		);
	}, [classes, media]);

	const mediaContent =
		isImage(media) || isPdf(media) ? (
			<ShowroomImage
				image={media}
				className={classes.previewMediaGridImg}
				label={displayLabel ? media.name : null}
				onClick={layerEnabled ? mediaActionHandler : null}
				attributes={{
					draggable: false,
					onContextMenu: ev => ev.preventDefault()
				}}
				{...imageProps}
			/>
		) : (
			<VideoPlayer
				id={`showroomMediaVideo_${media.id}`}
				videoId={media.cloudinaryId}
				cloudinaryVersion={media.cloudinaryVersion.toString()}
				config={{
					fullScreen: true,
					preview: !isMobileVersion,
					controls: isMobileVersion,
					muted: isMobileVersion,
					autoplayOnScroll: isMobileVersion
				}}
				onPlay={mediaActionHandler}
			/>
		);

	return (
		<div className={classes.container}>
			<ShowroomMediaActions
				media={media as any}
				// On mobile the zoom icon is not displayed even if there is a layer (zoom or product) enabled
				zoomButtonAction={isMobileVersion || !layerEnabled ? null : mediaActionHandler}
				displayLabel={displayLabel}
			>
				{mediaContent}
			</ShowroomMediaActions>
			{displayLabel && labelComponent}
		</div>
	);
};

export default React.memo(ShowroomMediaItem);
